import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchBlogByName, fetchBlogList } from "../services/blogApi";

const BlogContext = createContext(null);


const LIST_CACHE_KEY = "einsured_blog_list_v3";
const LIST_TTL_MS = 1000 * 60 * 60 * 6; // 6 hours


function readCache() {
  try {
    const raw = localStorage.getItem(LIST_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || !Array.isArray(parsed?.data)) return null;
    if (Date.now() - parsed.ts > LIST_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(LIST_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

export function BlogProvider({ children }) {
  const [posts, setPosts] = useState(() => readCache() || []);
  const [loading, setLoading] = useState(posts.length === 0);
  const [error, setError] = useState("");
  const [singleMap, setSingleMap] = useState(() => new Map());

  const refreshPosts = async () => {
    try {
      setError("");
      setLoading(true);
      const data = await fetchBlogList();
      setPosts(data);
      writeCache(data);
    } catch (e) {
      setError(e?.message || "Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  const getPost = async (name) => {
    if (!name) return null;
    if (singleMap.has(name)) return singleMap.get(name);

    const expectedId = posts.find((p) => p.name === name)?.id;
    const data = await fetchBlogByName(name, expectedId);

    setSingleMap((prev) => {
      const next = new Map(prev);
      next.set(name, data);
      return next;
    });

    return data;
  };

  useEffect(() => {
    
    if (posts.length === 0 || posts.length <= 12) refreshPosts();
    
  }, []);

  const value = useMemo(
    () => ({ posts, loading, error, refreshPosts, getPost }),
    [posts, loading, error, singleMap]
  );

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
}

export function useBlog() {
  const ctx = useContext(BlogContext);
  if (!ctx) throw new Error("useBlog must be used inside BlogProvider");
  return ctx;
}
